import { Save } from "lucide-react";
import { sideMeta, statusMap } from "./constants";
import Avatar from "@/components/Avatar";

function SideBtn({ side, active, onClick, testid }) {
  const meta = sideMeta[side];
  return (
    <button
      data-testid={testid}
      onClick={onClick}
      className={`px-3 py-1.5 rounded text-[10px] font-bold uppercase tracking-wide border ${
        active
          ? `${meta.chip} border-2`
          : `bg-slate-800 text-slate-400 border-transparent hover:${meta.darkBg}`
      }`}
    >
      {meta.label.replace("Les ", "")}
    </button>
  );
}

function TeamColumn({ side, players, captainId }) {
  const meta = sideMeta[side];
  return (
    <div
      data-testid={`team-column-${side}`}
      className="rounded-2xl bg-slate-900 border border-white/10 overflow-hidden"
    >
      <div
        className="p-4 border-b border-white/10 flex items-center gap-2"
        style={{ background: `${meta.color}15` }}
      >
        <span className="inline-block w-3 h-3 rounded-full" style={{ background: meta.color }} />
        <div className="font-display font-bold text-lg uppercase">{meta.label}</div>
        <span className="text-xs font-mono-stat text-slate-400 ml-auto">
          {players.length} joueur(s)
        </span>
      </div>
      <div className="divide-y divide-white/5">
        {players.length === 0 && (
          <div className="p-6 text-center text-slate-500 text-sm">Aucun joueur assigné</div>
        )}
        {players.map((p) => (
          <div key={p.id} className="p-3 flex items-center gap-3">
            <Avatar player={p} size={40} className="ring-1" />
            <div className="min-w-0 flex-1">
              <div className="font-bold text-sm truncate flex items-center gap-1.5">
                {p.name}
                {p.jersey_number != null && (
                  <span
                    className="text-[10px] font-mono-stat"
                    style={{ color: meta.color }}
                  >
                    #{p.jersey_number}
                  </span>
                )}
                {captainId === p.id && (
                  <span
                    data-testid={`captain-badge-${side}-${p.id}`}
                    title="Capitaine"
                    className="inline-flex items-center justify-center w-4 h-4 rounded text-[9px] font-black bg-[#E5B85C] text-[#0B0F17]"
                  >
                    C
                  </span>
                )}
              </div>
              <div className="text-xs text-slate-500 truncate">{p.position || "—"}</div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function CaptainsPicker({ rosterDraft, players, captainDraft, setCaptainDraft, onSave }) {
  const bySide = { bleus: [], rouges: [] };
  players.forEach((p) => {
    const s = rosterDraft[p.id];
    if (s === "bleus" || s === "rouges") bySide[s].push(p);
  });
  return (
    <div
      data-testid="captains-picker"
      className="rounded-2xl bg-slate-900 border border-white/10 p-5 space-y-4"
    >
      <div className="flex items-center gap-2">
        <span className="text-[11px] font-mono-stat uppercase tracking-widest text-[#E5B85C]">
          Capitaines
        </span>
        <span className="text-xs text-slate-500">
          · Un capitaine par équipe (parmi les joueurs assignés)
        </span>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {["bleus", "rouges"].map((side) => (
          <div key={side}>
            <label
              className="block text-xs font-mono-stat uppercase tracking-widest mb-2"
              style={{ color: sideMeta[side].color }}
            >
              Capitaine {sideMeta[side].label}
            </label>
            <select
              data-testid={`captain-select-${side}`}
              value={captainDraft[side] || ""}
              onChange={(e) =>
                setCaptainDraft({ ...captainDraft, [side]: e.target.value || null })
              }
              className="w-full px-3 py-2.5 bg-slate-800 border border-white/10 rounded-lg text-white text-sm"
            >
              <option value="">— Aucun —</option>
              {bySide[side].map((p) => (
                <option key={p.id} value={p.id}>
                  {p.jersey_number ? `#${p.jersey_number} ` : ""}
                  {p.name}
                </option>
              ))}
            </select>
          </div>
        ))}
      </div>
      <div className="flex justify-end">
        <button
          data-testid="save-captains-button"
          type="button"
          onClick={onSave}
          className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-[#E5B85C] hover:bg-[#D4A24E] text-[#0B0F17] text-xs font-bold uppercase tracking-wide"
        >
          Enregistrer les capitaines
        </button>
      </div>
    </div>
  );
}

export default function RosterTab({
  match,
  isCoach,
  present,
  rosterDraft,
  setDraftSide,
  saveRoster,
  balance,
  captainDraft,
  setCaptainDraft,
  saveCaptains,
  rosterByPlayer,
}) {
  const editing = isCoach && match.status !== "finished";
  return (
    <div className="space-y-4">
      {editing && (
        <div className="no-print flex flex-wrap gap-2 justify-between items-center rounded-xl bg-slate-900 border border-white/10 p-4">
          <div className="text-sm text-slate-300">
            Assigne chaque joueur présent{" "}
            <span className="text-amber-300 font-semibold">ou incertain</span> à une équipe.
          </div>
          <div className="flex gap-2">
            <button
              data-testid="balance-button"
              onClick={balance}
              className="px-3 py-2 rounded-lg bg-slate-800 hover:bg-slate-700 text-white text-xs font-bold uppercase tracking-wide"
            >
              Répartir équilibré
            </button>
            <button
              data-testid="save-roster-button"
              onClick={saveRoster}
              className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-[#E5B85C] hover:bg-[#D4A24E] text-[#0B0F17] font-bold text-xs uppercase tracking-wide"
            >
              <Save className="w-3.5 h-3.5" /> Enregistrer
            </button>
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <TeamColumn
          side="bleus"
          captainId={match.bleus_captain_id}
          players={(match.players || []).filter(
            (p) =>
              (editing ? rosterDraft[p.id] : rosterByPlayer[p.id]) === "bleus"
          )}
        />
        <TeamColumn
          side="rouges"
          captainId={match.rouges_captain_id}
          players={(match.players || []).filter(
            (p) =>
              (editing ? rosterDraft[p.id] : rosterByPlayer[p.id]) === "rouges"
          )}
        />
      </div>

      {editing && (
        <CaptainsPicker
          rosterDraft={rosterDraft}
          players={match.players || []}
          captainDraft={captainDraft}
          setCaptainDraft={setCaptainDraft}
          onSave={saveCaptains}
        />
      )}

      {editing && (
        <div className="rounded-2xl bg-slate-900 border border-white/10 overflow-hidden">
          <div className="p-4 border-b border-white/10 font-display font-bold text-sm uppercase text-slate-300 flex items-center gap-3 flex-wrap">
            <span>Joueurs à répartir · {present.length}</span>
            <span className="text-[10px] font-mono-stat tracking-widest text-emerald-300">
              {present.filter((a) => a.status === "available").length} Présent(s)
            </span>
            <span className="text-[10px] font-mono-stat tracking-widest text-amber-300">
              {present.filter((a) => a.status === "pending").length} Incertain(s)
            </span>
          </div>
          <div className="divide-y divide-white/5">
            {present.length === 0 && (
              <div className="p-6 text-center text-slate-500 text-sm">
                Aucun joueur n'a encore déclaré sa présence ou incertitude
              </div>
            )}
            {present.map((a) => {
              const p = (match.players || []).find((pp) => pp.id === a.player_id);
              if (!p) return null;
              const side = rosterDraft[p.id];
              const sMeta = statusMap[a.status];
              return (
                <div
                  key={p.id}
                  data-testid={`roster-assign-${p.id}`}
                  className="p-3 flex items-center justify-between gap-3"
                >
                  <div className="flex items-center gap-3 min-w-0">
                    <Avatar player={p} size={36} />
                    <div className="min-w-0">
                      <div className="font-bold text-sm truncate flex items-center gap-1.5 flex-wrap">
                        {p.name}
                        {p.jersey_number != null && (
                          <span className="text-[10px] font-mono-stat text-slate-500">
                            #{p.jersey_number}
                          </span>
                        )}
                        {sMeta && (
                          <span
                            data-testid={`roster-status-${a.status}-${p.id}`}
                            className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full text-[9px] font-mono-stat uppercase tracking-widest bg-${sMeta.color}-500/15 text-${sMeta.color}-300 border border-${sMeta.color}-500/30`}
                          >
                            <sMeta.Icon className="w-2.5 h-2.5" />
                            {sMeta.label}
                          </span>
                        )}
                      </div>
                      <div className="text-xs text-slate-500 truncate">{p.position || "—"}</div>
                    </div>
                  </div>
                  <div className="flex gap-1">
                    <SideBtn
                      side="bleus"
                      active={side === "bleus"}
                      onClick={() => setDraftSide(p.id, "bleus")}
                      testid={`assign-bleus-${p.id}`}
                    />
                    <SideBtn
                      side="rouges"
                      active={side === "rouges"}
                      onClick={() => setDraftSide(p.id, "rouges")}
                      testid={`assign-rouges-${p.id}`}
                    />
                    <button
                      data-testid={`unassign-${p.id}`}
                      onClick={() => setDraftSide(p.id, null)}
                      className={`px-2 py-1.5 rounded text-[10px] font-bold uppercase tracking-wide ${
                        !side
                          ? "bg-slate-700 text-white"
                          : "bg-slate-800 text-slate-400 hover:bg-slate-700"
                      }`}
                    >
                      —
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}
